import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ref, get, update } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { FaArrowLeft, FaPrint, FaFileAlt, FaSearch, FaTimes, FaClipboardList, FaBuilding, FaWrench } from 'react-icons/fa';
import { formatDate } from '../../Utils/dateUtils';
import { normalizeCompanyKey, firebaseCompanyKey } from '../../Utils/companyUtils';

const sanitizeKey = (key) => {
  return key.replace(/[.#$/[\]]/g, '_');
};

const inputClass = "w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block p-3 transition-all hover:border-indigo-300 shadow-sm";
const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";
const thClass = "p-4 border-b border-gray-100 font-semibold text-gray-600 bg-gray-50/80";

const GPEntryForm = () => {
  const { division } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const [company, setCompany] = useState('');
  const [jobSearch, setJobSearch] = useState('');
  const [numberOfJobs, setNumberOfJobs] = useState(1);
  const [jobSearchInputs, setJobSearchInputs] = useState(['']);
  const [jobSelected, setJobSelected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [address, setAddress] = useState('');

  const [commonData, setCommonData] = useState({
    date: new Date().toISOString().split('T')[0],
    vehicleNo: '',
  });

  const [formRows, setFormRows] = useState([
    { jobNo: '', kva: '', finalChallanNo: '', make: '', makeSrNo: '', type: '', oilCap: '' },
  ]);

  useEffect(() => {
    const stateCompany = location.state?.company;
    const storedCompany = localStorage.getItem('selectedCompany') || localStorage.getItem('userCompany');
    const selected = stateCompany || storedCompany;

    if (selected) {
      const sanitizedCompany = sanitizeKey(selected.trim());
      setCompany(sanitizedCompany);
      localStorage.setItem('selectedCompany', sanitizedCompany);
      
      const fetchAddress = async () => {
        const addrRef = ref(realtimeDb, `companyDetails/${sanitizedCompany}/address`);
        const addrSnap = await get(addrRef);
        setAddress(addrSnap.exists() ? addrSnap.val() : 'Address not available');
      };
      fetchAddress();

      // If coming from register with selected jobs
      if (location.state?.selectedJobs) {
        const rows = location.state.selectedJobs.map(job => ({
          id: job.id,
          jobNo: job.jobNo || '',
          kva: job.kva || 'N/A',
          make: job.make || 'N/A',
          makeSrNo: job.makeSrNo || 'N/A',
          type: job.type || 'N/A',
          oilCap: job.oilCap || '0.00',
          finalChallanNo: job.finalChallanNo || '',
        }));
        
        setFormRows(rows);
        setCommonData({
          date: new Date().toISOString().split('T')[0],
          vehicleNo: location.state.selectedJobs[0]?.vehicleNo || ''
        });
        setJobSelected(true);
      }
    } else {
      setError('Company not found. Please login again.');
      navigate('/login');
    }
  }, [location, navigate]);

  const handleJobSearch = async (e) => {
    e.preventDefault();
    const emptyInputs = jobSearchInputs.filter(input => !input.trim());
    if (emptyInputs.length > 0) {
      setError(`Please enter all ${numberOfJobs} job numbers`);
      return;
    }

    try {
      // Use sanitizeKey on division so path matches what EntryForm used to write
      const sanitizedDivision = sanitizeKey(division);
      const path = `companies/${company}/${sanitizedDivision}/register`;
      const entryRef = ref(realtimeDb, path);
      const snapshot = await get(entryRef);
      
      if (snapshot.exists()) {
        const entries = snapshot.val();
        let foundJobs = [];
        const notFoundJobs = [];
        
        for (let i = 0; i < jobSearchInputs.length; i++) {
          const searchTerm = jobSearchInputs[i].trim().toLowerCase();
          
          const specificJob = Object.entries(entries).find(
            ([id, entry]) => {
              const jobNo = entry.jobNo ? entry.jobNo.toString().trim().toLowerCase() : '';
              return jobNo === searchTerm && entry.completed;
            }
          );
          
          if (specificJob) {
            const [id, entry] = specificJob;
            const alreadyAdded = foundJobs.some(job => job.id === id);
            if (!alreadyAdded) {
              foundJobs.push({ id, entry });
            }
          } else {
            notFoundJobs.push(searchTerm);
          }
        }
        
        if (notFoundJobs.length > 0) {
          setError(`Jobs not found or not completed: ${notFoundJobs.join(', ')}`);
          if (foundJobs.length === 0) {
            setFormRows([{ jobNo: '', kva: '', finalChallanNo: '', make: '', makeSrNo: '', type: '', oilCap: '' }]);
            return;
          }
        }
        
        if (foundJobs.length > 0) {
          const rows = foundJobs.map((job, index) => {
            const challanNumber = job.entry.finalChallanNo ? job.entry.finalChallanNo.match(/\d+$/) : null;
            const incrementedChallan = challanNumber
              ? `CHLN-${parseInt(challanNumber[0]) + 1 + index}`
              : `CHLN-${1 + index}`;
              
            return {
              id: job.id,
              jobNo: job.entry.jobNo,
              kva: job.entry.kva || 'N/A',
              finalChallanNo: incrementedChallan,
              make: job.entry.make || 'N/A',
              makeSrNo: job.entry.makeSrNo || 'N/A',
              type: job.entry.type || 'N/A',
              oilCap: job.entry.oilCap || '0.00',
            };
          });
          
          setFormRows(rows);
          setCommonData((prev) => ({
            ...prev,
            vehicleNo: foundJobs[0].entry.vehicleNo || '',
          }));
          
          setJobSelected(true);
          if (notFoundJobs.length === 0) {
            setError(null);
          }
        } else {
          setError(`No jobs found from the search`);
          setFormRows([{ jobNo: '', kva: '', finalChallanNo: '', make: '', makeSrNo: '', type: '', oilCap: '' }]);
        }
      } else {
        setError('No entries found in register');
      }
    } catch (err) {
      setError('Failed to fetch job details: ' + err.message);
    }
  };

  const handleNumberOfJobsChange = (newNumber) => {
    setNumberOfJobs(newNumber);
    const newInputs = Array(newNumber).fill('').map((_, index) => 
      jobSearchInputs[index] || ''
    );
    setJobSearchInputs(newInputs);
  };

  const handleJobInputChange = (index, value) => {
    const newInputs = [...jobSearchInputs];
    newInputs[index] = value;
    setJobSearchInputs(newInputs);
  };

  const handleRowChange = (index, field, value) => {
    const newRows = [...formRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setFormRows(newRows);
  };

  const handleCommonChange = (e) => {
    const { name, value } = e.target;
    setCommonData((prev) => ({
      ...prev,
      [name]: name === 'vehicleNo' ? value.toUpperCase() : value,
    }));
  };

  const isFormValid = () => {
    const { date, vehicleNo } = commonData;
    if (!date || !vehicleNo) return false;
    for (let row of formRows) {
      if (!row.jobNo || !row.kva || !row.finalChallanNo || !row.make || !row.makeSrNo || !row.type || !row.oilCap) return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !isFormValid()) return;
    setIsSubmitting(true);
    setError(null);

    const { date, vehicleNo } = commonData;
    // Sanitize division so paths match what EntryForm wrote
    const sanitizedDivision = sanitizeKey(division);
    const gpEntryPath = `companies/${company}/${sanitizedDivision}/gpEntryPass`;
    const gpRegisterPath = `companies/${company}/${sanitizedDivision}/gpRegister`;
    const counterPath = `companies/${company}/${sanitizedDivision}/metadata/gpEntryCounter`;

    try {
      const counterRef = ref(realtimeDb, counterPath);
      const counterSnap = await get(counterRef);
      let counter = counterSnap.exists() ? counterSnap.val() : 0;

      const batchUpdates = {};
      
      for (const [index, row] of formRows.entries()) {
        counter += 1;
        
        const gpData = {
          ...row,
          date,
          vehicleNo,
          gpCreatedAt: new Date().toISOString(),
          srNo: index + 1,
          gpIssued: true,
          division: division,
          company: company,
          status: 'issued',
          completed: false
        };

        // Sanitize jobNo before using as Firebase key to avoid path corruption
        const jobKey = sanitizeKey(row.jobNo.toString().trim());

        batchUpdates[`${gpEntryPath}/${counter}`] = gpData;
        batchUpdates[`${gpRegisterPath}/${jobKey}`] = gpData;
        
        if (row.id) {
          batchUpdates[`companies/${company}/${sanitizedDivision}/register/${row.id}/gpIssued`] = true;
          batchUpdates[`companies/${company}/${sanitizedDivision}/register/${row.id}/gpCreatedAt`] = new Date().toISOString();
        }
      }

      batchUpdates[counterPath] = counter;
      await update(ref(realtimeDb), batchUpdates);

      setShowPrintPreview(true);
      setIsSubmitting(false);
    } catch (err) {
      console.error('Error saving GP entries:', err);
      setError('Failed to save GP Entry Pass: ' + err.message);
      setIsSubmitting(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) {
      setError('Cannot print: No content available');
      return;
    }
    setTimeout(() => {
      window.print();
      setShowPrintPreview(false);
    }, 500);
  };

  const totalOil = formRows.reduce((sum, row) => sum + (parseFloat(row.oilCap) || 0), 0);

  const renderPrintView = (copyTitle) => (
    <div className="bg-white text-base mb-10 px-10 py-8 border border-gray-400 leading-8 print-area"
      style={{ width: '210mm', minHeight: '297mm', boxSizing: 'border-box', pageBreakAfter: 'always' }}>
      <div className="text-center mb-6 leading-8">
        <h1 className="text-2xl font-bold uppercase">{company}</h1>
        <div className="text-lg font-medium">Transformer Service</div>
        <div className="text-lg font-semibold">{division} Division Gate Pass</div>
        <div className="text-base italic">{address}</div>
      </div>

      <div className="text-center mb-6 font-bold text-xl underline">GATE PASS - {copyTitle}</div>

      <div className="grid grid-cols-2 gap-4 text-base mb-6">
        <div><strong>Date:</strong> {formatDate(commonData.date) || 'N/A'}</div>
        <div><strong>Vehicle No:</strong> {commonData.vehicleNo || 'N/A'}</div>
        <div><strong>Challan No:</strong> {formRows[0]?.finalChallanNo || 'N/A'}</div>
        <div><strong>Total Oil:</strong> {totalOil.toFixed(2)} L</div>
      </div>

      <table className="w-full text-base border text-center mb-6 leading-8">
        <thead className="bg-gray-200">
          <tr>
            <th className="border p-2">SR No</th>
            <th className="border p-4">Job No</th>
            <th className="border p-2">KVA</th>
            <th className="border p-2">Make</th>
            <th className="border p-2">Make SR No</th>
            <th className="border p-2">Type</th>
            <th className="border p-2">Oil Cap</th>
          </tr>
        </thead>
        <tbody>
          {formRows.map((row, idx) => (
            <tr key={idx} className="border" style={{ lineHeight: '2.8' }}>
              <td className="border p-2">{idx + 1}</td>
              <td className="border p-2">{row.jobNo || 'N/A'}</td>
              <td className="border p-2">{row.kva || 'N/A'}</td>
              <td className="border p-2">{row.make || 'N/A'}</td>
              <td className="border p-2">{row.makeSrNo || 'N/A'}</td>
              <td className="border p-2">{row.type || 'N/A'}</td>
              <td className="border p-2">
                <input
                  type="text"
                  value={row.oilCap !== null && row.oilCap !== undefined ? row.oilCap : ''}
                  onChange={(e) => handleRowChange(idx, 'oilCap', e.target.value)}
                  className="w-full text-center border-none bg-transparent focus:ring-0 no-print"
                  style={{ outline: 'none', appearance: 'textfield' }}
                />
                <span className="print-only">{row.oilCap || '0.00'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-between text-lg mt-12">
        <div><strong>Sign of Receiver</strong></div>
        <div><strong>Sign of {company}</strong></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 lg:p-8 font-sans">
      <style>
        {`@media print {
          body { margin: 0; padding: 0; font-size: 16px; }
          .no-print { display: none !important; }
          .print-area-wrapper { display: block !important; margin: 0; padding: 0; }
          .print-area { display: block !important; page-break-after: always; }
          .print-only { display: block !important; }
        }
        .print-only { display: none; }
        `}
      </style>

      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-all hover:shadow-md no-print">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 rounded-full bg-gray-50 hover:bg-indigo-50 hover:text-indigo-600 text-gray-600 transition-colors"
            >
              <FaArrowLeft className="text-lg" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FaBuilding className="text-indigo-500" />
                {company}
              </h1>
              <h2 className="text-sm font-medium text-gray-500 flex items-center gap-2 mt-1">
                <FaWrench className="text-gray-400" />
                {division} <span className="mx-1">•</span> GP Entry Pass
              </h2>
            </div>
          </div>
          <button
            onClick={() => navigate(`/admin/division/${division}/gp-register`, { state: { company } })}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-gray-200 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 text-gray-700 rounded-xl font-medium transition-all shadow-sm"
          >
            <FaFileAlt />
            View GP Register
          </button>
        </div>

        {error && (
          <div className="no-print bg-red-50 border-l-4 border-red-500 p-4 rounded-xl shadow-sm flex items-start gap-3 text-red-700">
            <FaTimes className="text-xl shrink-0 mt-0.5" />
            <p className="font-medium text-sm">{error}</p>
          </div>
        )}

        {/* Form Container */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden no-print">
          
          <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
              <FaClipboardList />
            </div>
            <h3 className="font-semibold text-lg text-gray-800">Job Information</h3>
          </div>

          <div className="p-6 lg:p-8">
            {!jobSelected ? (
              <div className="max-w-4xl space-y-6">
                <div>
                  <label className={labelClass}>Number of Jobs to Search</label>
                  <select
                    value={numberOfJobs}
                    onChange={(e) => handleNumberOfJobsChange(parseInt(e.target.value))}
                    className={`${inputClass} w-full sm:w-48`}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num} Job{num > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className={labelClass}>Job Numbers to Search <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                    {jobSearchInputs.map((jobInput, index) => (
                      <div key={index} className="relative group">
                        <FaSearch className="absolute left-3.5 top-3.5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                        <input
                          type="text"
                          value={jobInput}
                          onChange={(e) => handleJobInputChange(index, e.target.value)}
                          placeholder={`Job ${index + 1} Number`}
                          className={`${inputClass} pl-10`}
                          required
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl text-indigo-800 text-sm">
                  <strong>Multiple Job Search:</strong> Enter {numberOfJobs} specific completed job number{numberOfJobs > 1 ? 's' : ''} to search and add to this GP Entry Pass.
                </div>
                
                <button
                  onClick={handleJobSearch}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl shadow-sm transition-all hover:shadow-indigo-200 hover:-translate-y-0.5"
                >
                  Search {numberOfJobs} Job{numberOfJobs > 1 ? 's' : ''}
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl">
                  <div>
                    <label className={labelClass}>Date</label>
                    <input
                      name="date"
                      type="date"
                      value={commonData.date}
                      onChange={handleCommonChange}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Vehicle No</label>
                    <input
                      name="vehicleNo"
                      placeholder="Vehicle No"
                      value={commonData.vehicleNo}
                      onChange={handleCommonChange}
                      className={`${inputClass} uppercase`}
                    />
                  </div>
                </div>

                <div className="overflow-hidden border border-gray-100 rounded-2xl shadow-sm">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr>
                        <th className={`${thClass} text-center w-16`}>#</th>
                        <th className={thClass}>Job No</th>
                        <th className={thClass}>KVA</th>
                        <th className={thClass}>Make</th>
                        <th className={thClass}>Make SR No</th>
                        <th className={thClass}>Type</th>
                        <th className={thClass}>Oil Cap</th>
                        <th className={thClass}>Final Challan No</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {formRows.map((row, idx) => (
                        <tr key={idx} className="bg-white hover:bg-gray-50/50 transition-colors">
                          <td className="p-4 text-center font-medium text-gray-500">{idx + 1}</td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={row.jobNo || ''}
                              onChange={(e) => handleRowChange(idx, 'jobNo', e.target.value)}
                              className="w-full bg-transparent border border-transparent hover:border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded p-1.5 transition-all text-gray-800 font-medium"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={row.kva || ''}
                              onChange={(e) => handleRowChange(idx, 'kva', e.target.value)}
                              className="w-full bg-transparent border border-transparent hover:border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded p-1.5 transition-all text-gray-600"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={row.make || ''}
                              onChange={(e) => handleRowChange(idx, 'make', e.target.value)}
                              className="w-full bg-transparent border border-transparent hover:border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded p-1.5 transition-all text-gray-600"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={row.makeSrNo || ''}
                              onChange={(e) => handleRowChange(idx, 'makeSrNo', e.target.value)}
                              className="w-full bg-transparent border border-transparent hover:border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded p-1.5 transition-all text-gray-600"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={row.type || ''}
                              onChange={(e) => handleRowChange(idx, 'type', e.target.value)}
                              className="w-full bg-transparent border border-transparent hover:border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded p-1.5 transition-all text-gray-600"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={row.oilCap || ''}
                              onChange={(e) => handleRowChange(idx, 'oilCap', e.target.value)}
                              className="w-full bg-transparent border border-transparent hover:border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded p-1.5 transition-all text-gray-600"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={row.finalChallanNo || ''}
                              onChange={(e) => handleRowChange(idx, 'finalChallanNo', e.target.value.toUpperCase())}
                              className="w-full bg-indigo-50 border border-indigo-100 hover:border-indigo-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded p-1.5 transition-all font-semibold text-indigo-700 uppercase"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100 justify-end">
                  <button
                    onClick={() => {
                      setJobSelected(false);
                      setJobSearch('');
                      setFormRows([{ jobNo: '', kva: '', finalChallanNo: '', make: '', makeSrNo: '', type: '', oilCap: '' }]);
                      setCommonData({ date: '', vehicleNo: '' });
                    }}
                    className="px-6 py-2.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-all shadow-sm"
                  >
                    Reset Search
                  </button>
                  <button
                    onClick={handleSubmit}
                    className={`px-8 py-2.5 rounded-xl font-medium text-white flex items-center justify-center gap-2 shadow-sm transition-all ${
                      isFormValid() && !isSubmitting
                        ? 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200 hover:-translate-y-0.5'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    disabled={!isFormValid() || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaPrint className="text-sm" /> 
                        Submit & Print
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="print-area-wrapper" ref={printRef}>
        {renderPrintView('Copy 1')}
        {renderPrintView('Copy 2')}
      </div>

      {showPrintPreview && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 no-print">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden transform transition-all">
            <div className="flex justify-between items-center bg-gray-50 border-b border-gray-100 p-5">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FaPrint className="text-indigo-600" />
                Print Preview
              </h3>
              <div className="flex space-x-3">
                <button
                  onClick={handlePrint}
                  className="flex items-center px-5 py-2 bg-indigo-600 text-white font-medium rounded-xl shadow-sm hover:bg-indigo-700 transition-all hover:-translate-y-0.5"
                >
                  <FaPrint className="mr-2" /> Print Now
                </button>
                <button
                  onClick={() => {
                    setShowPrintPreview(false);
                    setIsSubmitting(false);
                  }}
                  className="flex items-center px-5 py-2 bg-white text-gray-700 border border-gray-300 font-medium rounded-xl shadow-sm hover:bg-gray-50 transition-all"
                >
                  <FaTimes className="mr-2" /> Close
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1 bg-gray-100/50">
              <div className="bg-white shadow-sm border border-gray-200">
                {renderPrintView('Copy 1')}
              </div>
              <div className="mt-8 bg-white shadow-sm border border-gray-200">
                {renderPrintView('Copy 2')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GPEntryForm;